'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import Image from 'next/image'
import { X, ArrowLeft, ExternalLink, Star, Sparkles } from 'lucide-react'

interface Project {
  id: string
  name: string
  category: string
  description: string
  image: string
  url: string
  badge?: 'destaque' | 'novo'
}

const projects: Project[] = [
  {
    id: 'liboni-bauer',
    name: 'Liboni Bauer',
    category: 'Advocacia',
    description: 'Site institucional com design premium e SEO otimizado para escritorio de advocacia.',
    image: '/portfolio/liboni-bauer.webp',
    url: 'https://libonibauer.com.br',
    badge: 'destaque'
  },
  {
    id: 'tupa-fc',
    name: 'Tupa FC',
    category: 'Esportes',
    description: 'Portal completo para clube de futebol profissional com sistema de socios.',
    image: '/portfolio/tupa-fc.webp',
    url: 'https://tupafc.com.br',
    badge: 'novo'
  },
  {
    id: 'orestes',
    name: 'Orestes Cabeleireiro',
    category: 'Beleza & Estética',
    description: 'Landing page moderna com sistema de agendamento online integrado.',
    image: '/portfolio/orestes-cabeleireiro.webp',
    url: 'https://orestescabeleireiro.com.br'
  }
]

const badgeConfig = {
  destaque: {
    label: 'Destaque',
    icon: Star,
    className: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
  },
  novo: {
    label: 'Novo',
    icon: Sparkles,
    className: 'bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white'
  }
}

interface PortfolioModalProps {
  isOpen: boolean
  onClose: () => void
}

export function PortfolioModal({ isOpen, onClose }: PortfolioModalProps) {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)

  const handleClose = () => {
    setSelectedProject(null)
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4"
          onClick={handleClose}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 dark:bg-black/80 backdrop-blur-xl"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 30, stiffness: 400 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl h-full sm:h-auto sm:max-h-[90vh] overflow-hidden sm:rounded-2xl bg-white dark:bg-black border border-purple-500/20 dark:border-white/10 shadow-2xl shadow-purple-900/30"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-purple-500/20 dark:border-white/10 bg-indigo-950/5 dark:bg-white/5">
              <div className="flex items-center gap-3">
                {selectedProject ? (
                  <button
                    onClick={() => setSelectedProject(null)}
                    className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400 hover:text-indigo-950 dark:hover:text-white transition-colors"
                  >
                    <ArrowLeft size={16} />
                    <span>Voltar</span>
                  </button>
                ) : (
                  <>
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-500/20 dark:border-white/10 flex items-center justify-center">
                      <Sparkles size={18} className="text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-indigo-950 dark:text-white">Nosso Portfolio</h2>
                      <p className="text-xs text-indigo-950/60 dark:text-white/60">Projetos que criamos com amor</p>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={handleClose}
                className="w-11 h-11 rounded-xl bg-indigo-950/5 dark:bg-white/5 hover:bg-indigo-950/10 dark:hover:bg-white/10 border border-purple-500/20 dark:border-white/10 flex items-center justify-center text-purple-600 dark:text-purple-400 hover:text-indigo-950 dark:hover:text-white transition-all"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <AnimatePresence mode="wait">
                {selectedProject ? (
                  /* Project Detail View */
                  <motion.div
                    key="detail"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4 sm:space-y-6"
                  >
                    <div className="relative aspect-video rounded-xl overflow-hidden bg-indigo-950/5 dark:bg-white/5 border border-purple-500/20 dark:border-white/10">
                      <Image
                        src={selectedProject.image}
                        alt={selectedProject.name}
                        fill
                        sizes="(max-width: 768px) 100vw, 800px"
                        className="object-cover object-top"
                      />
                      {selectedProject.badge && (
                        <div className={`absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold shadow-lg ${badgeConfig[selectedProject.badge].className}`}>
                          {(() => {
                            const Icon = badgeConfig[selectedProject.badge].icon
                            return <Icon size={12} />
                          })()}
                          {badgeConfig[selectedProject.badge].label}
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-500/15 border border-purple-500/20 dark:border-white/10">
                        {selectedProject.category}
                      </span>
                      <h3 className="text-2xl font-bold text-indigo-950 dark:text-white">{selectedProject.name}</h3>
                      <p className="text-indigo-950/70 dark:text-white/70 leading-relaxed">{selectedProject.description}</p>
                    </div>

                    <a
                      href={selectedProject.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white font-medium rounded-full transition-all shadow-lg shadow-purple-500/30"
                    >
                      Visitar site
                      <ExternalLink size={16} />
                    </a>
                  </motion.div>
                ) : (
                  /* Grid View */
                  <motion.div
                    key="grid"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                  >
                    {projects.map((project, index) => (
                      <motion.button
                        key={project.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => setSelectedProject(project)}
                        className="group text-left rounded-xl overflow-hidden bg-indigo-950/5 dark:bg-white/5 border border-purple-500/20 dark:border-white/10 hover:border-purple-500/40 dark:hover:border-purple-500/30 hover:bg-indigo-950/10 dark:hover:bg-white/10 transition-all shadow-lg hover:shadow-purple-500/10"
                      >
                        <div className="relative aspect-video overflow-hidden">
                          <Image
                            src={project.image}
                            alt={project.name}
                            fill
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                            className="object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                          {project.badge && (
                            <div className={`absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold shadow-lg ${badgeConfig[project.badge].className}`}>
                              {(() => {
                                const Icon = badgeConfig[project.badge].icon
                                return <Icon size={10} />
                              })()}
                              {badgeConfig[project.badge].label}
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-purple-950/90 via-purple-950/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                            <span className="flex items-center gap-1 text-xs text-white font-medium">
                              Ver projeto
                              <ExternalLink size={12} />
                            </span>
                          </div>
                        </div>
                        <div className="p-4 bg-indigo-950/5 dark:bg-white/5">
                          <span className="text-[10px] text-purple-600 dark:text-purple-400 font-medium uppercase tracking-wider">{project.category}</span>
                          <h3 className="text-indigo-950 dark:text-white font-semibold mt-1 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                            {project.name}
                          </h3>
                          <p className="text-indigo-950/50 dark:text-white/50 text-xs mt-1 line-clamp-2">{project.description}</p>
                        </div>
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-purple-500/20 dark:border-white/10 bg-indigo-950/5 dark:bg-white/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-indigo-950/50 dark:text-white/50">
                  <span className="text-purple-600 dark:text-purple-400">★★★★★</span>
                  <span>+150 projetos entregues</span>
                </div>
                <a
                  href="https://biomo.com.br/portfolio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium transition-colors flex items-center gap-1"
                >
                  Ver todos
                  <ExternalLink size={12} />
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// Botao para abrir o modal - pode ser usado em qualquer lugar
export function PortfolioButton({ className = '' }: { className?: string }) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center gap-2 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium transition-colors ${className}`}
      >
        <Sparkles size={16} />
        Ver portfolio
      </button>
      <PortfolioModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  )
}
